import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BookOpen, AlertCircle, Users, Compass, Sparkles } from 'lucide-react';
import ServicesMarquee from '../components/ServicesMarquee';
import BackToTop from '../components/BackToTop';
import '../styles/FreeGuidePage.css';
import '../styles/EbookSalesPage.css';
import '../styles/LandingPage.css';

const FreeGuidePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Free Starter Guide | AfriCasa Behavioral Health';

    // Load MailerLite universal script once
    if (!window.ml) {
      const s = document.createElement('script');
      s.async = true;
      s.src = 'https://assets.mailerlite.com/js/universal.js';
      s.id = 'mailerlite-universal-script';
      document.head.appendChild(s);

      (function (w, d, e, u, f, l, n) {
        w[f] = w[f] || function () { (w[f].q = w[f].q || []).push(arguments); };
      })(window, document, 'script', 'https://assets.mailerlite.com/js/universal.js', 'ml');

      window.ml('account', '2407671');
    } else {
      try { window.ml('account', '2407671'); } catch (e) { /* no-op */ }
    }
  }, []);

  const scrollToForm = () => {
    const el = document.getElementById('free-guide-form');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="landing-page fg-page">
      {/* Header */}
      <header className="fg-header">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="logo-container fg-logo-link" data-testid="fg-home-link">
              <img
                src="https://customer-assets.emergentagent.com/job_pro-landing-page-21/artifacts/3l62dgff_AZ2m47AJpnyV8G0AjPPNlg-AZ2m47AJNCCxNcItlk4hCA.png"
                alt="AfriCasa Logo"
                className="logo"
              />
            </Link>
            <Link to="/" className="fg-back-link" data-testid="fg-back-link">
              <ArrowLeft size={18} />
              <span>BACK TO HOME</span>
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="fg-hero" data-testid="fg-hero">
        <div className="container mx-auto px-6">
          <div className="fg-hero-inner">
            <h1 className="fg-hero-title" data-testid="fg-hero-title">
              Your Clients Deserve Care That Actually Reaches Them.
            </h1>
            <div className="fg-divider" />
            <p className="fg-hero-subtitle">
              Download the free Cultural Competency Starter Guide — built for organizations and clinicians working with refugee and immigrant communities.
            </p>

            {/* Hero pricing-style card — matches the ebook page treatment */}
            <div className="ebook-hero-pricing-card" data-testid="fg-hero-pricing-card">
              <div className="ebook-hero-card-icon"><BookOpen size={26} /></div>
              <p className="ebook-final-price">FREE</p>
              <p className="ebook-final-meta">Instant PDF download</p>
              <a
                href="https://drive.google.com/file/d/1KB6c4lkqViuH00g1TDLv80swGUa8sgG6/view?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold ebook-final-cta-btn"
                data-testid="fg-hero-cta-btn"
              >
                Get The Free Guide
                <ArrowRight size={18} />
              </a>
            </div>

            <p className="fg-hero-note">No spam. Unsubscribe anytime.</p>
          </div>
        </div>
      </section>

      <hr className="afri-divider" data-testid="fg-divider-hero-reality" />

      {/* PROBLEM */}
      <section className="fg-section fg-section-problem" data-testid="fg-section-problem">
        <div className="container mx-auto px-6">
          <div className="fg-section-inner">
            <p className="fg-label">The Reality</p>
            <h2 className="fg-section-headline">
              Here&apos;s what nobody teaches you — in school or in practice.
            </h2>
            <div className="fg-section-body">
              <p>
                The client sitting across from you is not being resistant. Your intake form is in a language they do not speak. Your staff has never been trained on migration trauma. And the one bilingual person on your team is carrying the weight of an entire community.
              </p>
              <p className="fg-emphasis">
                That is not a client problem. That is a systems problem.
              </p>
              <p>
                Whether you lead an organization or you are a clinician on the front lines, this guide gives you a practical starting point.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT IS INSIDE */}
      <section className="fg-section fg-section-alt" data-testid="fg-section-walkaway">
        <div className="container mx-auto px-6">
          <div className="fg-section-inner">
            <p className="fg-label">Inside the Guide</p>
            <h2 className="fg-section-headline">What You Will Walk Away With</h2>
            <div className="fg-walkaway-grid">
              <div className="fg-walkaway-card">
                <div className="fg-walkaway-icon"><AlertCircle size={28} /></div>
                <h3 className="fg-walkaway-title">An Honest Self-Assessment</h3>
                <p className="fg-walkaway-text">A self-assessment for organizations on cultural competency.</p>
              </div>
              <div className="fg-walkaway-card">
                <div className="fg-walkaway-icon"><Users size={28} /></div>
                <h3 className="fg-walkaway-title">Cultural Considerations</h3>
                <p className="fg-walkaway-text">Practical cultural considerations for clinicians and providers.</p>
              </div>
              <div className="fg-walkaway-card">
                <div className="fg-walkaway-icon"><Compass size={28} /></div>
                <h3 className="fg-walkaway-title">Concrete Strategies</h3>
                <p className="fg-walkaway-text">Strategies you can apply immediately.</p>
              </div>
              <div className="fg-walkaway-card">
                <div className="fg-walkaway-icon"><Sparkles size={28} /></div>
                <h3 className="fg-walkaway-title">A Clear Next Step</h3>
                <p className="fg-walkaway-text">For going deeper with your practice or organization.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="fg-section fg-section-founder" data-testid="fg-section-founder">
        <div className="container mx-auto px-6">
          <div className="fg-section-inner fg-founder-inner">
            <h2 className="fg-founder-headline">
              I built AfriCasa because the communities I was serving deserved better than what existing systems were offering them.
            </h2>
            <div className="fg-founder-body">
              <p>
                I am Vanessa Diambou LMSW — with over a decade working across different populations and systems, now focused entirely on supporting organizations and clinicians who serve refugee and immigrant communities.
              </p>
              <p>
                I have seen what culturally responsive care changes — and what the absence of it costs. This guide is the starting point I wish more people had.
              </p>
            </div>
            <p className="fg-founder-signature">
              Vanessa Diambou LMSW · Founder AfriCasa Behavioral Health Consulting
            </p>
          </div>
        </div>
      </section>

      <hr className="afri-divider" data-testid="fg-divider-author-form" />

      {/* OPT-IN FORM (MailerLite) — at the bottom, ensures the form is the final call-to-action */}
      <section className="fg-section fg-section-form" id="free-guide-form" data-testid="fg-section-form">
        <div className="container mx-auto px-6">
          <div className="fg-form-wrapper">
            <p className="fg-label fg-label-centered">Get Instant Access</p>
            <h2 className="fg-form-headline">Download Your Free Starter Guide</h2>
            <p className="fg-form-subtext">
              Enter your information below. The guide will be delivered to your inbox immediately.
            </p>

            {/* MailerLite embedded form */}
            <div className="ml-embedded" data-form="oq8WfI" data-testid="mailerlite-embed"></div>

            <p className="fg-form-disclaimer">
              Your information is safe. No spam, ever.
            </p>
          </div>
        </div>
      </section>

      <hr className="afri-divider" data-testid="fg-divider-form-footer" />

      {/* Footer — mirrors the homepage footer */}
      <footer className="footer">
        <div className="container mx-auto px-6">
          <div className="footer-content">
            <div className="footer-logo">
              <img
                src="https://customer-assets.emergentagent.com/job_pro-landing-page-21/artifacts/3l62dgff_AZ2m47AJpnyV8G0AjPPNlg-AZ2m47AJNCCxNcItlk4hCA.png"
                alt="AfriCasa Logo"
                className="footer-logo-img"
              />
              <p className="footer-tagline">Culturally Rooted • Clinically Grounded</p>
            </div>

            <div className="footer-links">
              <div>
                <h4 className="footer-heading">Services</h4>
                <ul className="footer-list">
                  <li><Link to="/cultural-needs-assessment">Cultural Needs Assessment</Link></li>
                  <li><Link to="/cultural-competency-consulting">Cultural Competency Consulting</Link></li>
                  <li><Link to="/case-management">Organizational Case Management Services</Link></li>
                  <li><Link to="/concierge-case-management">Concierge Case Management Services</Link></li>
                  <li><Link to="/culturally-responsive-coaching">Culturally Responsive Coaching</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="footer-heading">Company</h4>
                <ul className="footer-list">
                  <li><Link to="/#about">About</Link></li>
                  <li><Link to="/#who-we-serve">Who We Serve</Link></li>
                  <li><Link to="/#services">Services</Link></li>
                  <li><Link to="/#certifications">Certifications</Link></li>
                  <li><Link to="/#reviews">Reviews</Link></li>
                  <li><Link to="/#contact">Contact</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="footer-heading">Contact</h4>
                <ul className="footer-list">
                  <li><a href="mailto:info@africasabehavioralhealth.org">info@africasabehavioralhealth.org</a></li>
                  <li><a href="tel:+18024885307">(802) 488-5307</a></li>
                  <li>Providence, RI</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>© 2026 AfriCasaRoots LLC (DBA AfriCasa Behavioral Health Consulting). All rights reserved.</p>
            <p className="footer-certifications">WOSB • EDWOSB • MBE Certification Pending</p>
          </div>
        </div>
      </footer>

      <hr className="afri-divider" data-testid="fg-divider-footer-marquee" />

      {/* Scrolling services marquee (matches homepage placement) */}
      <ServicesMarquee />

      <BackToTop />
    </div>
  );
};

export default FreeGuidePage;
